import { useState } from 'react'
import { useAddDomain } from '@/api/hooks/use-domains'
import { Modal } from '@/components/ui/modal'

interface AddDomainModalProps {
  open: boolean
  onClose: () => void
}

export function AddDomainModal({ open, onClose }: AddDomainModalProps): React.JSX.Element {
  const [domain, setDomain] = useState('')
  const [error, setError] = useState('')
  const addDomain = useAddDomain()

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    setError('')

    const trimmed = domain.trim().toLowerCase()
    if (!trimmed) {
      setError('Domain is required')
      return
    }

    try {
      await addDomain.mutateAsync({ domain: trimmed, region: 'eu' })
      setDomain('')
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add domain')
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add Domain">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <div>
          <label htmlFor="domain-name" className="block text-sm font-medium text-text-primary">
            Domain
          </label>
          <input
            id="domain-name"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="mail.yourdomain.com"
            className="mt-1 w-full rounded-lg border border-surface-3 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            Use a subdomain like mail.yourdomain.com for best deliverability
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={addDomain.isPending}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {addDomain.isPending ? 'Adding...' : 'Add Domain'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
